import {List, ListItem} from '@common/ui/list/list';
import {ReactNode, useContext, useState, useCallback, useEffect, Fragment} from 'react';
import { MarketRateDate } from './market-rate-date';
import moment from 'moment-timezone';
import { useAuth } from '@common/auth/use-auth';
import { setCookie } from '@common/utils/hooks/use-cookie';
import { CopyAllIcon } from '@common/icons/material/CopyAll';
import { DialogTrigger } from '@common/ui/overlays/dialog/dialog-trigger';
import { Button } from '@common/ui/buttons/button';
import { CopyRateSheetDialog } from './copy-rate-sheet';
import { useDeleteRateSheet } from '@app_modules/requests/use-delete-rate-sheet';
import {Tooltip} from '@common/ui/tooltip/tooltip';
import {DeleteIcon} from '@common/icons/material/Delete';
import {ConfirmationDialog} from '@common/ui/overlays/dialog/confirmation-dialog';
import {useNavigate} from '@common/utils/hooks/use-navigate';
import { IconButton } from '@common/ui/buttons/icon-button';
import { Trans } from '@common/i18n/trans';
import { DatatableDataQueryKey } from '@common/datatable/requests/paginated-resources';
import { queryClient } from '@common/http/query-client';


interface MarketRateSideLeftProps {
  marketRateDates: MarketRateDate[],
  changeDate: any,
  latestDate: any,
  changeDefaultTab: any
}

export function MarketRateSideLeft({
  marketRateDates,
  changeDate,
  latestDate,
  changeDefaultTab,
}: MarketRateSideLeftProps) {

  const {user} = useAuth();
  const user_id = user?.id;

  const [marketRateDate, setMarketRateDate] = useState(latestDate);

  const selectedDate = (market_rate_date: any) => {
    if (user_id && market_rate_date != latestDate) {
      setCookie('rate-sheet-tab-'+user_id, '0');
      changeDefaultTab(true);
    }

    changeDate(market_rate_date);
    setMarketRateDate(market_rate_date);

    if (user_id) {
      setCookie('market-rate-date-'+user_id, market_rate_date.toString());
    }
  }

  useEffect(() => {
    setMarketRateDate(latestDate);
  }, [latestDate]);

  const [rateDates, setRateDates] = useState(marketRateDates);
  const [dateArray, setDateArray] = useState(marketRateDates.map(item => item.created_date));

  const changeTargetDate = (targetDate: string) => {
    setMarketRateDate(targetDate);
  };

  const updateArrayDate = (dateArray: any[]) => {
    setRateDates(dateArray);
    setDateArray(dateArray.map(item => item.created_date));
  };

  useEffect(() => {
    setRateDates(rateDates);
  }, [rateDates]);

  useEffect(() => {
    setDateArray(dateArray);
  }, [dateArray]);
  
  return (
    <aside className="sticky top-10 flex-shrink-0 lg:block border-r-2 border-t-2">
      <List padding="p-0" className='scroll-p-8 overflow-y-scroll h-800 min-h-800 bg-gray-300'>
        {rateDates.map((item: MarketRateDate, index: number) => (
          <Item 
            key={item.created_date}
            panel={item.created_date}
            isSelected={marketRateDate==item.created_date}
            changeDate={selectedDate}
          >
          <div>
            <div className='h-30 rounded-panel shadow py-4 cursor-pointer float-left'>
              <span className={marketRateDate==item.created_date ? 'inline-block align-middle text-[#3B81F6]' : 'inline-block align-middle'}>
              <b>{moment.utc(item.created_date).format('MM/DD/YYYY')}</b>
              <Fragment>
                  <DialogTrigger type="modal" key={`dailog-${index}`}>
                    <Button size={'xs'} variant="outline" color="orange" className='mr-1 px-1 pl-0 pr-0'>
                      <CopyAllIcon/>
                    </Button>
                    <CopyRateSheetDialog key={`dailog-copy-${index}`} dateArray={dateArray} sourceDate={item.created_date} changeTargetDate={changeTargetDate}/>
                  </DialogTrigger>

                  {item.id ? (
                  <DeleteButton  id={ item.id } user_id={user_id} market_rate_date={item.created_date} dateArray={marketRateDates} changeTargetDate={changeTargetDate} updateArrayDate={updateArrayDate}/>
                ) : (<Fragment></Fragment>)}
              </Fragment>
              </span>
            </div>
          </div>
          </Item>
        ))}
        
      </List>
    </aside>
  );
}

interface ItemProps {
  children: ReactNode;
  isSelected?: boolean;
  panel: string;
  changeDate: any,
}
function Item({children, isSelected, panel, changeDate}: ItemProps) {
  return (
    <ListItem
      className={isSelected ?'bg-[#F4F8FE]' : undefined}
      padding="px-0 py-10 justify-start"
      onSelected={() => {
        changeDate(panel);
      }}
    >
      {children}
    </ListItem>
  );
}


interface DeleteButtonProps {
  id: number;
  user_id?: number;
  market_rate_date: string;
  dateArray: any[];
  changeTargetDate: any;
  updateArrayDate: any;
}
function DeleteButton({ id, user_id, dateArray, market_rate_date, changeTargetDate, updateArrayDate }: DeleteButtonProps) {
 const deleteItem = useDeleteRateSheet();
 return (
   <DialogTrigger
     type="modal"
     onClose={confirmed => {
       if (confirmed) {
        deleteItem.mutate({marketRateId:  id}, {
          onSuccess: (response: any) => {
            const arra_date = dateArray.filter((item: any) => item.created_date !==market_rate_date);
            updateArrayDate(arra_date);
            const lastItem = arra_date[arra_date.length -1];
            const lastDate = lastItem.created_date;
            changeTargetDate(lastDate);
            if (user_id) {
              setCookie('market-rate-date-'+user_id, lastDate.toString());
            }
            queryClient.invalidateQueries({queryKey: [`market-rates/${lastDate}`]});
            queryClient.invalidateQueries({queryKey: [`market-rates-date`]});
            
          },
        });

       }
     }}
   >
     <Tooltip label={<Trans message="Delete chat" />}>
       <IconButton
         size="md"
         className="pl-0 pr-0 justify-start"
         disabled={deleteItem.isPending}
       >
         <DeleteIcon className='pl-0 pr-0'/>
       </IconButton>
     </Tooltip>
     <ConfirmationDialog
       title={<Trans message="Delete" />}
       body={<Trans message="Are you sure you want to delete this item?" />}
       confirm={<Trans message="Delete" />}
     />
   </DialogTrigger>
 );
}